import React, { useEffect } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import { blogPosts } from "../data/blogData";
import { Container, Row, Col, Button, Image, Accordion, Card, ButtonGroup } from 'react-bootstrap';
import { Facebook, Linkedin, Link as LinkIcon, Whatsapp } from 'react-bootstrap-icons';
import { FaXTwitter } from 'react-icons/fa6'; // Using react-icons for X (Twitter) icon
import "../styles/blogs.css";

const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const blog = blogPosts.find(post => post.slug === slug);

  // Helper function to get absolute URL
  const getAbsoluteUrl = (path) => {
    if (!path) return '';
    return path.startsWith('http') ? path : `${window.location.origin}${path}`;
  };

  // Current page URL and metadata
  const currentUrl = window.location.href;
  const absoluteImageUrl = getAbsoluteUrl(blog?.image);
  const shareTitle = encodeURIComponent(blog?.title || '');
  const shareText = encodeURIComponent(
    `Check out this blog post: ${blog?.title || ''}\n\n${currentUrl}`
  );

  // Set up meta tags for social sharing
  useEffect(() => {
    if (!blog) return;

    const setMetaTag = (property, content) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    // Required OpenGraph tags
    setMetaTag('og:title', blog.title);
    setMetaTag('og:description', blog.contentSections?.[0]?.intro || blog.title);
    setMetaTag('og:image', absoluteImageUrl);
    setMetaTag('og:url', currentUrl);
    setMetaTag('og:type', 'article');
    setMetaTag('og:image:width', '1200');
    setMetaTag('og:image:height', '630');

    // Twitter Card tags
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', blog.title);
    setMetaTag('twitter:description', blog.contentSections?.[0]?.intro || blog.title);
    setMetaTag('twitter:image', absoluteImageUrl);

  }, [blog, currentUrl, absoluteImageUrl]);

  if (!blog) return <p>Blog not found.</p>;

  // Social share URLs with proper parameters
  const socialLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`,
    x: `https://twitter.com/intent/tweet?text=${shareTitle}&url=${encodeURIComponent(currentUrl)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`,
    whatsapp: `https://wa.me/?text=${shareText}`
  };

  // Copy rich content to clipboard
  const copyToClipboard = () => {
    const richText = `${blog.title}\n\n${currentUrl}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(richText)
        .then(() => alert('Blog post link copied to clipboard!'))
        .catch(err => console.error('Failed to copy: ', err));
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = richText;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        alert('Blog post link copied to clipboard!');
      } catch (err) {
        alert('Failed to copy link');
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <section className="py-4">
      <Container>
        <Button variant="outline-secondary" onClick={() => navigate("/blogs")} className="mb-3">
          ← Back to Blog List
        </Button>

        <Row className="mb-4">
          <Col>
            <Image 
              src={blog.image} 
              alt={blog.title} 
              fluid 
              rounded 
              className="blog-featured-image"
              onError={(e) => {
                e.target.onerror = null; 
                e.target.src = '/default-blog-image.jpg';
              }}
            />
          </Col>
        </Row>

        <Row>
          <Col xs={12} md={7} lg={8}>
            <h1 className="blog-title">{blog.title}</h1>
            <p className="blog-meta">
              <small>By {blog.author} on {blog.date}</small>
            </p>
            <h6>Share this post:</h6>
            <div className="social-share">
              
              <ButtonGroup aria-label="Social share buttons" className="flex-wrap">
                <Button 
                  variant="outline-primary" 
                  onClick={() => window.open(socialLinks.facebook, '_blank', 'width=600,height=400')}
                  aria-label="Share on Facebook"
                  className="share-btn"
                >
                  <Facebook className="me-1" />
                </Button>
                <Button 
                  variant="outline-dark" // Changed to dark for X
                  onClick={() => window.open(socialLinks.x, '_blank', 'width=600,height=400')}
                  aria-label="Share on X"
                  className="share-btn"
                >
                  <FaXTwitter className="me-1" />
                </Button>
                <Button 
                  variant="outline-primary" 
                  onClick={() => window.open(socialLinks.linkedin, '_blank', 'width=600,height=400')}
                  aria-label="Share on LinkedIn"
                  className="share-btn"
                >
                  <Linkedin className="me-1" />
                </Button>
                <Button 
                  variant="outline-success" 
                  onClick={() => window.open(socialLinks.whatsapp, '_blank', 'width=600,height=400')}
                  aria-label="Share on WhatsApp"
                  className="share-btn"
                >
                  <Whatsapp className="me-1" /> 
                </Button>
                <Button 
                  variant="outline-secondary" 
                  onClick={copyToClipboard}
                  aria-label="Copy link"
                  className="share-btn"
                >
                  <LinkIcon className="me-1" />
                </Button>
              </ButtonGroup>
            </div>

            {blog.contentSections?.map((section, index) => (
              <div id={section.id} key={index} className="scroll-section mb-4">
                <p>{section.intro}</p>
                <h4>{section.title}</h4>
                <p>{section.description}</p>
                <p>{section.extraParagraph}</p>
                {section.listing}
                <p>{section.afterULParagraph}</p>
              </div>
            ))}

            {blog.faq && blog.faq.length > 0 && (
              <>
                <h4 className="mt-5">Frequently Asked Questions (FAQs)</h4>
                <Accordion defaultActiveKey={null} flush>
                  {blog.faq.map((item, index) => (
                    <Accordion.Item eventKey={index.toString()} key={index}>
                      <Accordion.Header>{item.question}</Accordion.Header>
                      <Accordion.Body>{item.answer}</Accordion.Body>
                    </Accordion.Item>
                  ))}
                </Accordion>
              </>
            )}
          </Col>
          
          <Col xs={12} md={5} lg={4} className="sticky-top">
            <Card className="tableOfContent">
              <Card.Body>
                {blog.listItems && blog.listItems.length > 0 && (
                  <>
                    <h4 className="heading">Table of Content</h4>
                    <ul>
                      {blog.listItems.map((item, index) => (
                        <li key={index}>
                          <a href={`#${blog.contentSections[index].id}`} className="toc-link">
                            {item}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default BlogDetail;